import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import {
  Button,
  ButtonRadio,
  Divider,
  Loading,
  message,
  Modal,
  Pagination,
  Trans,
} from '@/components'
import { ActionMenu } from '@/components/base/ActionMenu'
import { CollapseTableRow } from '@/components/CollapseTableRow.tsx'
import { EmptyView } from '@/components/EmptyView'
import { getScaleCreatePath, useUnifiedContext } from '@/contexts/UnifiedContext'
import { ScaleSpec } from '@/models/scaleSpec.ts'
import ScalingSpecDetail from '@/pages/auto-scaling/components/ScalingSpecDetail.tsx'
import { formatPrice } from '@/utils/formatter.ts'

interface FilterItem {
  label: string
  value: string
  codes: boolean
}

export function AutoScalingListPage() {
  const navigate = useNavigate()
  const ctx = useUnifiedContext()
  const scaleCreatePath = getScaleCreatePath(ctx)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [scaleSpecList, setScaleSpecList] = useState<ScaleSpec[]>([])
  const pageSize = 10
  const { t } = useTranslation(['scaling', 'translation'])

  const statusFilters: FilterItem[] = [
    {
      label: t('type.running'),
      value: 'running',
      codes: true,
    },
    {
      label: t('type.suspended'),
      value: 'suspended',
      codes: false,
    },
  ]
  const [filter, setFilter] = useState<FilterItem | undefined>(statusFilters[0])

  const filteredScaleSpecList = useMemo(() => {
    if (filter === undefined) return scaleSpecList
    return scaleSpecList.filter((spec) => spec.enabled === filter.codes)
  }, [scaleSpecList, filter])

  const getScaleSpecList = useCallback(
    async (p = page) => {
      setLoading(true)
      try {
        const res = await API.getScaleSpecList({
          page: p,
          per_page: pageSize,
          enabled: filter?.codes,
        })
        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)
          setScaleSpecList(res.list.map((spec) => new ScaleSpec(spec)))
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [page, pageSize, filter],
  )

  const onToggleScaleSpec = (spec: ScaleSpec) => {
    API.toggleScaleSpec({ spec_id: spec.resID, enabled: !spec.enabled }).then(() => {
      getScaleSpecList(page)
      message.success(<Trans i18nKey="common.message.success" ns="translation" />)
    })
  }

  const onDeleteScaleSpec = (spec: ScaleSpec) => {
    Modal.confirm({
      title: t('deleteModal.title'),
      content: (
        <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
          <div className="t-sm font-medium text-secondary flex items-center">
            <Trans i18nKey="deleteModal.content" ns="scaling">
              You are about to delete this rule permanently. Are you sure?
            </Trans>
          </div>
        </div>
      ),
      confirmBtnCfg: {
        type: 'danger',
        text: t('deleteModal.confirmBtn'),
      },
      className: 'w-[640px]',
    }).then(() => {
      API.deleteScaleSpec({ spec_id: spec.resID }).then(() => {
        message.success(<Trans i18nKey="common.message.success" ns="translation" />)
        getScaleSpecList()
      })
    })
  }

  useEffect(() => {
    getScaleSpecList(1)
  }, [])

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <div className="flex items-start justify-between mb-4">
        <div className="h6">
          <Trans i18nKey="title" ns="scaling">
            Autoscaling
          </Trans>
        </div>
        <Link to={scaleCreatePath}>
          <Button type="secondary">
            <Trans i18nKey="createBtn" ns="scaling">
              New Scaling Rule
            </Trans>
          </Button>
        </Link>
      </div>

      <Divider />

      <ButtonRadio
        value={filter?.value}
        items={statusFilters}
        onChange={(e) => {
          setFilter(statusFilters.find((i) => i.value === e))
        }}
      />

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!loading && filteredScaleSpecList.length > 0 && (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderID" ns="scaling">
                  ID
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderName" ns="scaling">
                  Name
                </Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="tableHeaderStatus" ns="scaling">
                  Status
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderCost" ns="scaling">
                  Cost
                </Trans>
              </th>
              <th colSpan={3}>
                <Trans i18nKey="tableHeaderLastExeTime" ns="scaling">
                  Last Execution Time
                </Trans>
              </th>
              <th colSpan={3}>
                <Trans i18nKey="tableHeaderAction" ns="scaling">
                  Action
                </Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredScaleSpecList.map((spec) => (
              <CollapseTableRow
                key={spec.resID}
                colSpan={13}
                tableClassName={'!border-none'}
                content={<ScalingSpecDetail scalingSpecResId={spec.resID} />}
              >
                <td colSpan={2} className="t-sm">
                  {spec.resID}
                </td>
                <td colSpan={2}>{spec.class}</td>
                <td colSpan={1}>{spec.enabled ? t('type.running') : t('type.suspended')}</td>
                <td colSpan={2}>{formatPrice(0)}</td>
                <td colSpan={3}>{dayjs(spec.createTime).format('YYYY-MM-DD hh:mm:ss')}</td>
                <td colSpan={2}>
                  <ActionMenu
                    items={[
                      {
                        label: (
                          <Trans i18nKey="action.edit" ns="scaling">
                            Edit
                          </Trans>
                        ),
                        onClick: () =>
                          navigate(scaleCreatePath, { state: { editingSpecResId: spec.resID } }),
                        type: 'highlight',
                      },
                      {
                        label: spec.enabled ? (
                          <Trans i18nKey="action.suspend" ns="scaling">
                            Suspend
                          </Trans>
                        ) : (
                          <Trans i18nKey="action.resume" ns="scaling">
                            Resume
                          </Trans>
                        ),
                        onClick: () => onToggleScaleSpec(spec),
                        type: 'highlight',
                      },
                      {
                        label: (
                          <Trans i18nKey="action.delete" ns="scaling">
                            Delete
                          </Trans>
                        ),
                        onClick: () => onDeleteScaleSpec(spec),
                        type: 'danger',
                      },
                    ]}
                    popupContainer="body"
                  />
                </td>
              </CollapseTableRow>
            ))}
          </tbody>
        </table>
      )}
      {!loading && filteredScaleSpecList.length > 0 && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={(page) => getScaleSpecList(page)}
        />
      )}
      {!loading && filteredScaleSpecList.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}
    </div>
  )
}
